// src/pages/LoginRedirect.js
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { setAuth } from '../utils/auth';
import { fetchAndStoreUmatPermissions  } from '../utils/wkCommon'

const backendUrl = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

const LoginRedirect = (props) => {
  const [text, setText] = useState('Loading...');
  const location = useLocation();
  const params = useParams();
  const history = useHistory();

  useEffect(() => {
    let mounted = true;

    async function finishLogin() {
      try {
        const fetchUrl = `${backendUrl}/api/auth/${params.providerName}/callback${location.search}`;
        const res = await fetch(fetchUrl);
        if (res.status !== 200) {
          const body = await res.text().catch(() => '');
          throw new Error(`Couldn't login to Strapi. Status: ${res.status} - ${body}`);
        }
        const data = await res.json();

        // data should contain { jwt, user }
        const jwt = data.jwt;
        const userFromStrapi = data.user || null;

        if (!jwt || !userFromStrapi) {
          throw new Error('Missing jwt or user in Strapi response');
        }

        // backward compat for other code
        localStorage.setItem('jwt', jwt);
        localStorage.setItem('lefy_auth', jwt);
        if (userFromStrapi.email) localStorage.setItem('email', userFromStrapi.email);
        if (userFromStrapi.username) localStorage.setItem('username', userFromStrapi.username);

        // Try to fetch umat record by LoginEmail (if exists)
        let umatRecord = null;
        try {
          if (userFromStrapi.email) {
            // Build safe encoded query params
            const params = new URLSearchParams();
            params.set('filters[LoginEmail][$eq]', userFromStrapi.email);
            //params.set('populate', '*'); // adjust populate fields if needed
            params.set('populate[0]', 'avatarpic');
            params.set('populate[1]', 'profilepic');
            params.set('populate[2]', 'umat_roles');
            params.set('populate[3]', 'umat_roles.umat_permissions');
            params.set('populate[4]', 'area');

            const umatUrl = `${backendUrl}/api/umats?${params.toString()}`;

            // attach Authorization header if jwt present (some Strapi setups require auth to query collection)
            const headers = {};
            const token = localStorage.getItem('jwt');
            if (token) headers['Authorization'] = `Bearer ${token}`;

            const r = await fetch(umatUrl, { headers });
            // log response body for debugging when not OK
            const bodyText = await r.clone().text().catch(() => '');
            //console.debug('fetch umat:', umatUrl, 'status:', r.status, 'body:', bodyText);

            if (!r.ok) {
              // do not throw the whole login; just warn and continue
              console.warn('Failed to fetch umat by email', r.status, bodyText);
            } else {
              const j = await r.json();
              const arr = j?.data || j?.umats || null;
              if (Array.isArray(arr) && arr.length > 0) {
                umatRecord = arr[0];
                try { localStorage.setItem('umatData', JSON.stringify(umatRecord)); } catch (e) { /* ignore */ }
                const kode = umatRecord?.umatID
                if (kode) localStorage.setItem('umatID', String(kode));
              }
            }
          }
        } catch (e) {
          // catch any error in umat fetch but don't abort login flow
          console.warn('Error while fetching umat record:', e);
        }

        // build final user object and save canonical auth
        const finalUser = {
          ...userFromStrapi,
          umat: umatRecord || null,
        };
        // store unified auth object used oleh getAuthRaw() / getUser() di MasterLayout
        setAuth({ jwt, user: finalUser });

        if (finalUser?.email) {
          await fetchAndStoreUmatPermissions(finalUser.email);
        }

        if (!mounted) return;

        setText('You have been successfully logged in. Redirecting...');
        // replace so back-button doesn't go to callback
        history.replace('/');
      } catch (err) {
        console.error('LoginRedirect error:', err);
        if (mounted) setText('An error occurred while logging you in. See the console for details.');
      }
    }

    finishLogin();
    return () => { mounted = false; };
  }, [history, location.search, params.providerName]);

  return <p>{text}</p>;
};

export default LoginRedirect;
